import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState } from 'react';
import type { FamilyTreeNode } from '../interfaces';
import { getNodeCenter, getNodeCenters } from '../utils';

const ALIGN_THRESHOLD = 10;

interface SnapState {
    x: number | null;
    y: number | null;
}

export function useHelperLines(setNodes: Dispatch<SetStateAction<FamilyTreeNode[]>>) {
    const [activeHelpers, setActiveHelpers] = useState<string[]>([]);
    const [snap, setSnap] = useState<SnapState>({ x: null, y: null });

    const showHelperLines = useCallback(
        (draggedNode: FamilyTreeNode, nodes: FamilyTreeNode[]) => {
            const nodeCenters = getNodeCenters(nodes);
            const draggedNodeCenter = getNodeCenter(draggedNode);
            const snappedDraggedCenter = nodeCenters.get(draggedNode.id);
            const helperNodes: FamilyTreeNode[] = [];
            let snapX: number | null = null;
            let snapY: number | null = null;

            nodes.forEach((node) => {
                if (node.id === draggedNode.id || node.id.startsWith('helper-')) return;

                const center = nodeCenters.get(node.id);
                if (!center) return;

                const dx = Math.abs(center.x - draggedNodeCenter.x);
                const dy = Math.abs(center.y - draggedNodeCenter.y);

                if (dx < ALIGN_THRESHOLD) {
                    snapX = center.x;
                    const startY = Math.min(snappedDraggedCenter ? snappedDraggedCenter.y : draggedNodeCenter.y, center.y);
                    const helperLineLength = snappedDraggedCenter ? Math.abs(center.y - snappedDraggedCenter.y) : dy;

                    helperNodes.push({
                        id: 'helper-x',
                        type: 'helperLineNode',
                        position: { x: center.x, y: startY },
                        data: {
                            axis: 'x',
                            length: helperLineLength,
                            color: 'var(--fg-color-danger-normal)',
                            thickness: 1,
                            isConnector: false,
                            usedHandles: [],
                        },
                        style: { zIndex: 1000 },
                        selectable: false,
                        draggable: false,
                    });
                }
                if (dy < ALIGN_THRESHOLD) {
                    const startX = Math.min(snappedDraggedCenter ? snappedDraggedCenter.x : draggedNodeCenter.x, center.x);
                    const helperLineLength = snappedDraggedCenter ? Math.abs(center.x - snappedDraggedCenter.x) : dx;

                    snapY =
                        node.data.isConnector && draggedNode.data.isConnector
                            ? center.y
                            : node.data.isConnector
                              ? center.y - 1
                              : draggedNode.data.isConnector
                                ? center.y + 1
                                : center.y;

                    helperNodes.push({
                        id: 'helper-y',
                        type: 'helperLineNode',
                        position: { x: startX, y: center.y },
                        data: {
                            axis: 'y',
                            length: helperLineLength,
                            color: 'var(--fg-color-danger-normal)',
                            thickness: 1,
                            isConnector: false,
                            usedHandles: [],
                        },
                        style: { zIndex: 1000 },
                        selectable: false,
                        draggable: false,
                    });
                }
            });

            setActiveHelpers(helperNodes.map((n) => n.id));
            setSnap({ x: snapX, y: snapY });

            setNodes((nds) => [
                // Remove any old helper line nodes
                ...nds.filter((n) => !activeHelpers.includes(n.id)),
                ...helperNodes,
            ]);
        },
        [activeHelpers, setNodes],
    );

    const removeHelperLines = useCallback(() => {
        setNodes((prev) => prev.filter((n) => !activeHelpers.includes(n.id)));
        setActiveHelpers([]);
    }, [activeHelpers, setActiveHelpers, setNodes]);

    return { showHelperLines, removeHelperLines, snap };
}
